import React, { useMemo } from 'react';
import { Brain, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import Card from '../../../components/Card';
import EnhancedSurveyResults from '../EnhancedSurveyResults';
import CreativeReviewNew from '../../../components/CreativeReviewNew';
import AmazonSearchResults from '../../../components/AmazonSearchResults';
import ABTestCreativesResults from '../../../components/abtest/ABTestCreativesResults';
import PackagingReviewV2Results from './PackagingReviewV2Results';
import PackagingVariantComparisonResults from './PackagingVariantComparisonResults';
import ConceptTestResults from './ConceptTestResults';
import DrivingConceptTestResults from './DrivingConceptTestResults';
import VitaminConceptTestResults from './VitaminConceptTestResults';
import ClaimsTestingResults from './ClaimsTestingResults';
import BarChartComponent from '../../../components/BarChart';
import LineChartComponent from '../../../components/LineChart';
import PieChart from '../../../components/PieChart';
import RadarChart from '../../../components/RadarChart';
import HorizontalBarChart from '../../../components/HorizontalBarChart';
import EnhancedTableView from '../../../components/EnhancedTableView';
import SimulationInputsDropdown from './SimulationInputsDropdown';
import { parseJsonOrRepair } from '@/utils/parseJsonOrRepair';

interface SimulationData {
  id: number;
  name?: string;
  source_model: string;
  created_at: string;
  personas: Array<{
    id: number;
    name: string;
    data: any;
  }>;
  segments?: Array<{
    id: number;
    name: string;
  }>;
}

interface ContentData {
  [key: string]: unknown;
  task?: string;
  images?: string[];
  image?: string[];
  image_descriptions?: string[];
  questions?: unknown[];
}

interface ParsedResponse {
  output: string;
  analysis: string;
  tables: TableData[];
  hasJsonBlock: boolean;
  winner?: number;
  scores?: number[];
}

interface TableData {
  type: 'bar' | 'line' | 'table' | 'pie' | 'radar' | 'horizontal_chart';
  title?: string;
  data: any[];
  xAxis?: string;
  yAxis?: string;
  headers?: string[];
  nameKey: string;
  valueKey: string;
  messages?: { text: string; percentage?: number; score?: number }[];
}

interface SimulationAnalysisPanelProps {
  simulation: SimulationData;
  simulationResponse: string;
  contentData: ContentData;
  isPreparingExport: boolean;
  expandedCards: Record<string, boolean>;
  onToggleCard: (cardName: string) => void;
  isDetailsDropdownOpen: boolean;
  onCloseDropdown: () => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  onImageClick: (src: string, alt: string) => void;
  isPrintMode?: boolean;
  isPublicView?: boolean;
  /** Marketplace concept reports must not show the vitamin category NORMS (rank/percentile,
   *  reference products, and the norm population's N) — that data benchmarks against the
   *  external vitamin panel, not the user's own study, so it reads as a confusing mismatch
   *  (e.g. "reference products: Collagen broth" / "N=126" next to the user's protein-bar run). */
  marketplaceView?: boolean;
  isEmbedded?: boolean;
  simulationId?: string | number;
  /**
   * Render the inputs as an always-present page section above the results instead of a popover
   * opened from the toolbar. Used by the rating view, where reading the inputs is step one of the
   * task rather than an occasional lookup.
   */
  inputsInline?: boolean;
  /** Inline mode only: whether the inputs body is expanded. */
  inputsOpen?: boolean;
  /** Inline mode only: toggle the inputs body. */
  onToggleInputs?: () => void;
  /**
   * Inline mode only: place the inputs in a column beside the results rather than stacked above
   * them, so a rater can read the brief and the output together without scrolling between them.
   * Collapsing the column hands its width to the results.
   *
   * Applies from `md` up only. Two columns on a phone would leave neither readable, so below the
   * breakpoint this falls back to the stacked layout.
   */
  inputsSideBySide?: boolean;
  /** Side-by-side mode only: restrict the inputs to author-provided fields. */
  userInputsOnly?: boolean;
}

const SimulationAnalysisPanel: React.FC<SimulationAnalysisPanelProps> = ({
  simulation,
  simulationResponse,
  contentData,
  isPreparingExport,
  expandedCards,
  onToggleCard,
  isDetailsDropdownOpen,
  onCloseDropdown,
  dropdownRef,
  onImageClick,
  inputsInline = false,
  inputsOpen = true,
  onToggleInputs,
  inputsSideBySide = false,
  userInputsOnly = false,
  marketplaceView = false,
  isPrintMode = false,
  // isPublicView is still accepted by callers, but no longer read: the public view used
  // to be the only one that let the report flow below md. Every view does now.
  isEmbedded = false,
  simulationId,
}) => {
  const parseSimulationResponse = (response: string): ParsedResponse => {
    const jsonBlockMatch = response.match(/((\[[^\}]{3,})?\{\s*[^\}\{]{3,}?:.*\}([^\{]+\])?)/);
    if (jsonBlockMatch) {
      try {
        const jsonContent = jsonBlockMatch[1];
        let parsedData = JSON.parse(jsonContent);
        if (Array.isArray(parsedData)) {
          for (const item of parsedData) {
            if (item.output) {
              parsedData = item;
              break;
            }
          }
        }

        if (typeof parsedData.tables === 'string') {
          try {
            parsedData.tables = parsedData.tables
              .replace(/```json/g, '')
              .replace(/```/g, '')
              .replace(/\n/g, '')
              .replace(/\r/g, '');
            parsedData.tables = parseJsonOrRepair(parsedData.tables);
          } catch (error) {
            console.error('Error parsing tables:', error);
          }
        } else if (parsedData.tables && !Array.isArray(parsedData.tables)) {
          parsedData.tables = [parsedData.tables];
        }

        return {
          output: parsedData.output || '',
          analysis: parsedData.analysis || '',
          tables: parsedData.tables || [],
          hasJsonBlock: true,
          winner: parsedData.winner,
          scores: parsedData.scores,
        };
      } catch (error) {
        console.error('Error parsing JSON block:', error);
        return {
          output: response,
          analysis: '',
          tables: [],
          hasJsonBlock: false,
          winner: undefined,
          scores: undefined,
        };
      }
    } else {
      try {
        let parsedData = parseJsonOrRepair(response);
        if (Array.isArray(parsedData)) {
          for (const item of parsedData) {
            if (item.output) {
              parsedData = item;
              break;
            }
          }
        }

        if (typeof parsedData.tables === 'string') {
          try {
            parsedData.tables = parsedData.tables
              .replace(/```json/g, '')
              .replace(/```/g, '')
              .replace(/\n/g, '')
              .replace(/\r/g, '');
            parsedData.tables = parseJsonOrRepair(parsedData.tables);
          } catch (error) {
            console.error('Error parsing tables:', error);
          }
        } else if (Array.isArray(parsedData.tables)) {
          parsedData.tables = parsedData.tables.map((table: any) => {
            if (typeof table === 'string') {
              table = table
                .replace(/```json/g, '')
                .replace(/```/g, '')
                .replace(/\n/g, '')
                .replace(/\r/g, '');
              return parseJsonOrRepair(table);
            }
            return table;
          });
        } else if (parsedData.tables && !Array.isArray(parsedData.tables)) {
          parsedData.tables = [parsedData.tables];
        }

        return {
          output: parsedData.output || '',
          analysis: parsedData.analysis || '',
          tables: parsedData.tables || [],
          hasJsonBlock: true,
          winner: parsedData.winner,
          scores: parsedData.scores,
        };
      } catch (error) {
        console.error('Error parsing JSON block:', error);
        return {
          output: response,
          analysis: '',
          tables: [],
          hasJsonBlock: false,
          winner: undefined,
          scores: undefined,
        };
      }
    }
  };

  const renderTables = (
    tables: TableData[],
    contentData?: ContentData,
  ): React.ReactElement | null => {
    if (!tables || tables.length === 0) return null;

    const convertDataToNumbers = (data: any[]): any[] => {
      if (!data) return [];
      return data.map((item) => {
        const convertedItem: Record<string, any> = {};
        Object.entries(item).forEach(([key, value]) => {
          if (typeof value === 'string' && !isNaN(Number(value))) {
            convertedItem[key] = Number(value);
          } else if (typeof value === 'number') {
            convertedItem[key] = value;
          } else {
            convertedItem[key] = value;
          }
        });
        return convertedItem;
      });
    };

    return (
      <div className="mt-6 space-y-6 mb-3">
        {tables.map((table: TableData, index: number) => {
          const formattedData =
            table.type !== 'horizontal_chart' ? convertDataToNumbers(table.data) : [];

          return (
            <div
              key={index}
              className="bg-white m-3 rounded-lg border border-gray-200 shadow-sm overflow-hidden"
            >
              {table.type === 'horizontal_chart' && table.messages && (
                <div className="p-4">
                  <HorizontalBarChart
                    data={table.messages.map((msg) => ({
                      name: msg.text,
                      value: msg.score || msg.percentage || 0,
                    }))}
                    title={table.title}
                  />
                </div>
              )}

              {table.type === 'bar' && table.data && table.xAxis && table.yAxis && (
                <div className="px-4 py-1 mb-10">
                  <div className="h-96 mb-16">
                    <BarChartComponent
                      data={formattedData}
                      xAxis={table.xAxis}
                      yAxis={table.yAxis}
                      title={table.title}
                    />
                  </div>
                </div>
              )}

              {table.type === 'line' && table.data && table.xAxis && table.yAxis && (
                <div className="p-4">
                  <div className="h-64">
                    <LineChartComponent
                      data={formattedData}
                      xAxis={table.xAxis}
                      yAxis={table.yAxis}
                      title={table.title}
                    />
                  </div>
                </div>
              )}

              {table.type === 'pie' && table.data && table.nameKey && table.valueKey && (
                <div className="p-4">
                  <div className="h-64">
                    <PieChart
                      data={formattedData}
                      nameKey={table.nameKey}
                      valueKey={table.valueKey}
                      title={table.title}
                    />
                  </div>
                </div>
              )}

              {table.type === 'radar' && table.data && table.nameKey && (
                <div className="p-4">
                  <div className="h-64">
                    <RadarChart data={formattedData} nameKey={table.nameKey} title={table.title} />
                  </div>
                </div>
              )}

              {table.type === 'table' &&
                table.data &&
                table.headers &&
                (() => {
                  const images = contentData?.images || contentData?.image || [];

                  return (
                    <EnhancedTableView
                      headers={table.headers}
                      data={table.data}
                      title={table.title}
                      pdfMode={false}
                      showExportButtons={true}
                      showFullContent={true}
                      images={images || []}
                      isExporting={isPreparingExport}
                    />
                  );
                })()}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSimulationContent = (parsedResponse: ParsedResponse, contentData: ContentData) => {
    const isSurveyTask = contentData?.task === 'survey_and_focus_groups';
    const hasEnhancedFormat =
      simulationResponse &&
      (simulationResponse.includes('survey_results') ||
        simulationResponse.includes('"total_questions"') ||
        simulationResponse.includes('"question_id"'));

    if (isSurveyTask && hasEnhancedFormat) {
      try {
        const surveyData = JSON.parse(simulationResponse);
        if (surveyData.survey_results) {
          return (
            <div className="p-6">
              <EnhancedSurveyResults
                data={surveyData}
                contentType={contentData?.task}
                isExporting={isPreparingExport}
                images={contentData?.images || []}
                questions={contentData?.questions || []}
                onImageClick={onImageClick}
                surveyName={
                  (contentData as any)?.name ||
                  (contentData as any)?.audience_name ||
                  simulation?.name
                }
                surveyGoal={(contentData as any)?.goal || (contentData as any)?.goal_of_simulation}
                surveyContext={(contentData as any)?.context}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Error parsing survey data:', error);
      }
    }

    const isCreativeReviewTask = contentData?.task === 'creative-review';
    if (isCreativeReviewTask && simulationResponse) {
      try {
        const creativeReviewData = JSON.parse(simulationResponse);
        if (creativeReviewData.json_output || creativeReviewData.markdown_output) {
          const creativeReviewDataWithImages = {
            ...creativeReviewData,
            images: contentData?.image || contentData?.images || [],
            image_descriptions: contentData?.image_descriptions || [],
          };

          return (
            <div className="p-6">
              <CreativeReviewNew
                data={creativeReviewDataWithImages}
                isExporting={isPreparingExport}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse creative review format:', error);
      }
    }

    const isAmazonSearchTask = contentData?.task === 'amazon-search';
    if (isAmazonSearchTask && simulationResponse) {
      try {
        const amazonSearchData = JSON.parse(simulationResponse);
        if (
          amazonSearchData.json_response ||
          amazonSearchData.output ||
          amazonSearchData.analysis
        ) {
          return (
            <div className="p-6">
              <AmazonSearchResults
                data={amazonSearchData}
                contentData={contentData}
                isExporting={isPreparingExport}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse amazon search format:', error);
      }
    }

    const isABTestCreativesTask = contentData?.task === 'channel-event-strategy';
    if (isABTestCreativesTask && simulationResponse) {
      try {
        const abTestData = JSON.parse(simulationResponse);
        if (abTestData.output && abTestData.analysis) {
          const abTestDataWithImages = {
            ...abTestData,
            images: contentData?.image || contentData?.images || [],
            image_descriptions: contentData?.image_descriptions || [],
          };

          return (
            <div className="p-6">
              <ABTestCreativesResults
                data={abTestDataWithImages}
                contentType={contentData?.task}
                isExporting={isPreparingExport}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse AB test creatives format:', error);
      }
    }

    const isPackagingReviewV2Task = contentData?.task === 'packaging-review-v2';
    if (isPackagingReviewV2Task && simulationResponse) {
      try {
        const packagingV2Data = JSON.parse(simulationResponse);
        if (
          packagingV2Data.final_insights ||
          packagingV2Data.technical_compliance_insights ||
          packagingV2Data.shelf_test_insights ||
          packagingV2Data.persona_reaction_insights ||
          packagingV2Data.element_drivers
        ) {
          return (
            <div
              className={`p-3 md:p-6 ${isPrintMode ? 'print-mode overflow-visible h-auto' : ''}`}
            >
              <PackagingReviewV2Results
                data={packagingV2Data}
                contentData={contentData}
                isPreparingExport={isPreparingExport}
                onImageClick={onImageClick}
                isPrintMode={isPrintMode}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse packaging review v2 format:', error);
      }
    }

    // Packaging Variant Comparison task
    const isVariantComparisonTask = contentData?.task === 'packaging-variant-comparison';
    if (isVariantComparisonTask && simulationResponse) {
      try {
        const variantData = JSON.parse(simulationResponse);
        if (variantData.variants && variantData.winner) {
          return (
            <div className={`p-6 ${isPrintMode ? 'print-mode overflow-visible h-auto' : ''}`}>
              <PackagingVariantComparisonResults
                data={variantData}
                contentData={contentData}
                isPreparingExport={isPreparingExport}
                isPrintMode={isPrintMode}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse variant comparison format:', error);
      }
    }

    // Driving Concept Test task
    if (contentData?.task === 'driving-concept-test' && simulationResponse) {
      return (
        <div className="p-6">
          <DrivingConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
          />
        </div>
      );
    }

    // Vitamin Concept Test task
    if (contentData?.task === 'vitamin-concept-test' && simulationResponse) {
      return (
        <div className={`p-6 ${isPrintMode ? 'print-mode overflow-visible h-auto' : ''}`}>
          <VitaminConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
            shareToken={typeof simulationId === 'string' ? simulationId : undefined}
            isPrintMode={isPrintMode}
          />
        </div>
      );
    }

    // Concept Test task
    const isConceptTestTask = contentData?.task === 'concept-test';
    if (isConceptTestTask && simulationResponse) {
      return (
        <div className="p-6">
          <ConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
            onImageClick={onImageClick}
          />
        </div>
      );
    }

    // Claims Testing task
    if (contentData?.task === 'claims_testing' && simulationResponse) {
      return (
        <div className="p-6">
          <ClaimsTestingResults simulationResponse={simulationResponse} contentData={contentData} />
        </div>
      );
    }

    // Driving Concept Test task
    const isDrivingConceptTestTask = contentData?.task === 'driving-concept-test';
    if (isDrivingConceptTestTask && simulationResponse) {
      return (
        <div className="p-6">
          <DrivingConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
          />
        </div>
      );
    }

    // Legacy rendering for non-special tasks
    return (
      <div className="p-4">
        {/* Only render non-table charts if present */}
        {parsedResponse.tables &&
          parsedResponse.tables.length > 0 &&
          parsedResponse.tables.some((table: TableData) => table.type !== 'table') &&
          renderTables(parsedResponse.tables.filter((table: TableData) => table.type !== 'table'))}

        <MarkdownRenderer
          content={parsedResponse.output}
          variant="default"
          bordered={true}
          images={contentData?.images || []}
          isExporting={isPreparingExport}
        />

        {/* Only render table type tables if present */}
        {parsedResponse.tables &&
          parsedResponse.tables.length > 0 &&
          parsedResponse.tables.some((table: TableData) => table.type === 'table') &&
          renderTables(
            parsedResponse.tables.filter((table: TableData) => table.type === 'table'),
            contentData,
          )}

        {/* Only render analysis if present */}
        {parsedResponse.hasJsonBlock && parsedResponse.analysis && (
          <div className="mt-6">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => onToggleCard('analysis')}
                className="flex items-center justify-between w-full text-left p-4 bg-gradient-to-r from-gray-50 to-white hover:from-primary-light hover:to-white transition-all duration-200 group"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 mr-3 group-hover:border-primary/20 transition-colors">
                    <Brain className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <span className="text-base font-medium text-text-primary">
                      Detailed Analysis
                    </span>
                    <p className="text-xs text-text-secondary mt-0.5">
                      In-depth insights and recommendations
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-text-secondary font-medium">
                    {expandedCards.analysis ? 'Hide Details' : 'View Details'}
                  </span>
                  {expandedCards.analysis ? (
                    <ChevronUp className="h-4 w-4 text-primary" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-primary" />
                  )}
                </div>
              </button>

              {expandedCards.analysis && (
                <div className="border-t border-gray-100 p-6 bg-white">
                  <MarkdownRenderer
                    content={parsedResponse.analysis}
                    variant="default"
                    bordered={false}
                    images={contentData?.images || []}
                    isExporting={isPreparingExport}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // parseSimulationResponse runs a heavy regex + JSON.parse over the (large) response string.
  // Memoize it on the response so it runs only when the response changes, not on every render —
  // otherwise transient re-renders during load each redo seconds of work and lock the tab.
  const parsedResponse = useMemo(
    () => parseSimulationResponse(simulationResponse || ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [simulationResponse],
  );

  // Extract norms data from vitamin concept test response
  const extractNormsData = ():
    | {
        category_mean?: Record<string, number>;
        reference_products?: Array<{
          name: string;
          k1?: number;
          k2?: number;
          k3?: number;
          k4?: number;
        }>;
        concept_vs_norm?: Record<string, string>;
        concept_rank?: number;
        concept_percentile?: number;
      }
    | undefined => {
    if (contentData?.task !== 'vitamin-concept-test' || !simulationResponse) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(simulationResponse);

      // Debug: log the parsed structure to help identify where norms is

      // Check multiple possible locations for norms
      // 1. Direct norms at root level
      if (parsed.norms) {
        return parsed.norms;
      }

      // 2. Inside results object
      if (parsed.results?.norms) {
        return parsed.results.norms;
      }

      // 3. Inside report object (single concept)
      if (parsed.report?.norms) {
        return parsed.report.norms;
      }

      // 4. Inside report_list (multi-concept) - check first report
      if (
        parsed.report_list &&
        Array.isArray(parsed.report_list) &&
        parsed.report_list.length > 0
      ) {
        const firstReport = parsed.report_list[0];
        if (firstReport?.norms) {
          return firstReport.norms;
        }
        if (firstReport?.report?.norms) {
          return firstReport.report.norms;
        }
      }

      // 5. Check concepts array for multi-concept
      if (parsed.concepts && Array.isArray(parsed.concepts) && parsed.concepts.length > 0) {
        const firstConcept = parsed.concepts[0];
        if (firstConcept?.norms) {
          return firstConcept.norms;
        }
      }
    } catch (e) {
      console.error('[Norms Debug] Parse error:', e);
    }
    return undefined;
  };

  // Suppress the vitamin category-norms card on marketplace concept reports (see marketplaceView
  // prop): its reference products and norm-population N=126 belong to the external panel, not the
  // user's study, and clash with the report's own audience/sample size.
  const normsData = marketplaceView ? undefined : extractNormsData();

  return (
    <div
      id="simulation-analysis-section"
      // The 90vh height cap makes the report its own scroll box, which is right on a
      // desktop where it sits beside the chat. On a phone it turned the whole report
      // into a ~538px keyhole inside an 844px screen while the page itself did not
      // scroll. Below md the report flows with the page (the public view already did
      // this); from md up nothing changes.
      className={`${
        isPrintMode || isEmbedded
          ? 'h-auto overflow-visible'
          : 'h-auto overflow-visible md:h-full md:overflow-auto md:max-h-[calc(90vh-120px)]'
      }`}
    >
      <div className="mb-6">
        {(() => {
          const inputsPanel = (inputsInline || isDetailsDropdownOpen) && (
            <div ref={dropdownRef}>
              <SimulationInputsDropdown
                simulation={simulation}
                contentData={contentData}
                isOpen={inputsInline ? inputsOpen : isDetailsDropdownOpen}
                onClose={inputsInline ? (onToggleInputs ?? onCloseDropdown) : onCloseDropdown}
                onImageClick={onImageClick}
                expandedCards={expandedCards}
                onToggleCard={onToggleCard}
                normsData={normsData}
                variant={inputsInline ? 'inline' : 'overlay'}
                userInputsOnly={userInputsOnly}
                marketplaceView={marketplaceView}
              />
            </div>
          );

          const resultsCard = (
            <Card
              className={`border border-gray-200 shadow-sm ${
                isPrintMode ? 'overflow-visible' : 'overflow-hidden'
              }`}
            >
              <div className="bg-white">{renderSimulationContent(parsedResponse, contentData)}</div>
            </Card>
          );

          // Printing must stay a single linear column — a floated sidebar breaks pagination.
          if (!inputsSideBySide || !inputsInline || isPrintMode) {
            return (
              <>
                {inputsPanel}
                {resultsCard}
              </>
            );
          }

          return (
            <div className="md:flex md:gap-4 md:items-start">
              {/* The panel keeps ONE stable slot in both states and is hidden with CSS when
                  collapsed, rather than being moved into a different branch. Moving it remounted
                  the subtree on every toggle, silently discarding disclosure state held inside it
                  ("Other inputs", per-image "Show More") while parent-held state survived — an
                  inconsistency that read as a glitch.

                  Collapsed, `md:hidden` hides it from md up (the rail takes over) but leaves it
                  visible below md, where the rail is not rendered and this header is the only way
                  back to the inputs. */}
              <div className={inputsOpen ? 'md:w-[380px] md:shrink-0' : 'md:hidden'}>
                {inputsPanel}
              </div>

              {!inputsOpen && (
                <div className="hidden md:block md:shrink-0">
                  <button
                    type="button"
                    onClick={onToggleInputs}
                    aria-expanded={false}
                    aria-label="Expand simulation inputs"
                    className="w-11 bg-white border border-gray-200 rounded-xl py-3 flex flex-col
                               items-center gap-3 hover:bg-gray-50 hover:border-blue-300
                               transition-colors"
                  >
                    <ChevronRight className="h-4 w-4 text-blue-700" aria-hidden="true" />
                    <span
                      className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-600"
                      style={{ writingMode: 'vertical-rl' }}
                    >
                      Inputs
                    </span>
                  </button>
                </div>
              )}

              {/* min-w-0 lets the results column actually shrink; without it long tables and
                  code blocks force the flex row wider than the page. */}
              <div className="md:flex-1 md:min-w-0 mt-4 md:mt-0">{resultsCard}</div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SimulationAnalysisPanel;
